import {
  STATUS_SBL_ACTIVATED,
  STATUS_SBL_AWAITING_SIGNATURE,
  STATUS_SBL_BANKID_REJECTED,
  STATUS_SBL_DONE,
  STATUS_SBL_DRAFT,
  STATUS_SBL_ONGOING,
  STATUS_SBL_PLANNED,
  STATUS_SBL_SIGNED,
} from '~/constants/contract'

/**
 * Lifecycle order of StatusSbl.
 *
 * The CRM option-set values are NOT ordered: STATUS_SBL_BANKID_REJECTED (735300008) is numerically
 * above STATUS_SBL_DONE, and future values (e.g. quote statuses) can land anywhere. Never compare
 * StatusSbl with <, <=, > or >=. Use the predicates below.
 *
 * Each entry is one stage. Statuses in the same entry are at the same stage:
 * BANKID_REJECTED behaves like AWAITING_SIGNATURE (sent to the forest owner, not signed; saving
 * triggers a new signature process). Future quote statuses go in front of DRAFT.
 */
export const STATUS_SBL_STAGES: ReadonlyArray<ReadonlyArray<number>> = [
  [STATUS_SBL_DRAFT],
  [STATUS_SBL_AWAITING_SIGNATURE, STATUS_SBL_BANKID_REJECTED],
  [STATUS_SBL_SIGNED],
  [STATUS_SBL_PLANNED],
  [STATUS_SBL_ACTIVATED],
  [STATUS_SBL_ONGOING],
  [STATUS_SBL_DONE],
]

/** Index of the stage the status belongs to, or -1 for null/undefined/unknown values. */
export const getStatusSblStage = (status: number | null | undefined): number =>
  status === null || status === undefined ? -1 : STATUS_SBL_STAGES.findIndex(stage => stage.includes(status))

/** True when the status is at `milestone`'s stage or a later one. Unknown status → false. */
export const isStatusSblAtLeast = (status: number | null | undefined, milestone: number): boolean => {
  const stage = getStatusSblStage(status)
  const milestoneStage = getStatusSblStage(milestone)
  return stage >= 0 && milestoneStage >= 0 && stage >= milestoneStage
}

/** True when the status is at an earlier stage than `milestone`. Unknown status → false. */
export const isStatusSblBefore = (status: number | null | undefined, milestone: number): boolean => {
  const stage = getStatusSblStage(status)
  const milestoneStage = getStatusSblStage(milestone)
  return stage >= 0 && milestoneStage >= 0 && stage < milestoneStage
}

/** True when the status is at exactly the same stage as `milestone`. Unknown status → false. */
export const isStatusSblSameStage = (status: number | null | undefined, milestone: number): boolean => {
  const stage = getStatusSblStage(status)
  return stage >= 0 && stage === getStatusSblStage(milestone)
}
